import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { registerPatient, getPublicCentreInfo } from '../utils/api';
import { FiUser, FiPhone, FiMail, FiLock, FiTag } from 'react-icons/fi';
import { INDIAN_STATES } from '../constants/indianStates';

// Evaluated once at module load — userAgent is stable for the session.
// iOS: AASA / Android: App Links redirect app-installed users to native,
// so this page is only reached by users who do NOT have the app installed —
// app store banners are always relevant for them.
const isIOS       = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
const isAndroid   = /Android/.test(navigator.userAgent || '');
const appStoreUrl  = process.env.REACT_APP_APP_STORE_URL  || '';
const playStoreUrl = process.env.REACT_APP_PLAY_STORE_URL || '';

const Field = ({ icon, label, name, type = 'text', placeholder, value, onChange, required }) => (
  <div style={S.field}>
    <label style={S.label}>{label}</label>
    <div style={S.inputWrap}>
      <span style={S.inputIcon}>{icon}</span>
      <input
        style={S.input} name={name} type={type}
        placeholder={placeholder} value={value} onChange={onChange}
        required={required}
        autoComplete={type === 'password' ? 'new-password' : undefined}
      />
    </div>
  </div>
);

const RegisterPatient = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const params     = new URLSearchParams(window.location.search);
  const centreCode = params.get('centre') || '';
  const ref        = params.get('ref')    || '';
  // support both ?invitedBy= (WhatsApp invite URL) and ?referredBy= (referral links)
  const invitedBy    = params.get('invitedBy') || params.get('referredBy') || '';
  // referralCode closes the ReferralLog conversion loop (registerPatient.js:151)
  const referralCode = params.get('referralCode') || '';

  const [centreName,  setCentreName]  = useState('');
  const [form,        setForm]        = useState({ firstName: '', lastName: '', phone: '', email: '', password: '', confirmPassword: '', state: '' });
  const [error,       setError]       = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  useEffect(() => {
    if (!centreCode) return;
    (async () => {
      try {
        const res = await getPublicCentreInfo(centreCode);
        setCentreName(res.data.data?.centre?.tenantName || centreCode);
      } catch {
        setCentreName(centreCode);
      }
    })();
  }, [centreCode]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!centreCode)  { setError('Centre code is missing. Please use the invite link from your centre.'); return; }
    if (!form.state)  { setError('Please select your state.'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await registerPatient({
        firstName: form.firstName, lastName: form.lastName,
        phone:     form.phone,     email:    form.email,
        password:     form.password,  centreCode,
        state:        form.state,
        invitedBy:    invitedBy    || undefined,
        referralCode: referralCode || undefined,
      });
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <img src="/logo.png" alt="LifeChiAI" style={S.logo} />
        <h1 style={S.heading}>
          {centreName ? `Join ${centreName} on LifeChiAI` : 'Join LifeChiAI'}
        </h1>
        {ref === 'invite' && (
          <div style={S.badge}>
            <FiTag size={13} style={{ marginRight: 6 }} />
            Invited by your centre
          </div>
        )}
        {referralCode && (
          <div style={S.referralBadge}>
            <FiTag size={13} style={{ marginRight: 6, flexShrink: 0 }} />
            You were referred to LifeChiAI. Completing registration will record this referral.
          </div>
        )}
        {isIOS && appStoreUrl && (
          <div style={S.appBanner}>
            <span style={S.appBannerText}>Get the LifeChiAI app for a better experience</span>
            <a href={appStoreUrl} style={S.appBannerBtn} target="_blank" rel="noopener noreferrer">
              Download on the App Store
            </a>
          </div>
        )}
        {isAndroid && playStoreUrl && (
          <div style={S.appBanner}>
            <span style={S.appBannerText}>Get the LifeChiAI app for a better experience</span>
            <a href={playStoreUrl} style={S.appBannerBtn} target="_blank" rel="noopener noreferrer">
              Get it on Google Play
            </a>
          </div>
        )}
        {error && <div style={S.errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={S.row}>
            <Field icon={<FiUser size={14} />} label="First name" name="firstName" value={form.firstName} onChange={set('firstName')} required />
            <Field icon={<FiUser size={14} />} label="Last name"  name="lastName"  value={form.lastName}  onChange={set('lastName')}  required />
          </div>
          <Field icon={<FiPhone size={14} />} label="Phone"    name="phone"    type="tel"      placeholder="+91 98765 43210" value={form.phone}    onChange={set('phone')}    required />
          <Field icon={<FiMail  size={14} />} label="Email"    name="email"    type="email"    value={form.email}            onChange={set('email')}    required />
          <Field icon={<FiLock  size={14} />} label="Password" name="password" type="password" value={form.password}         onChange={set('password')} required />
          <Field icon={<FiLock  size={14} />} label="Confirm password" name="confirmPassword" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required />
          <div style={S.field}>
            <label style={S.label}>State *</label>
            <select name="state" value={form.state} onChange={set('state')} style={{ ...S.input, paddingLeft: '34px' }} required>
              <option value="">Select state</option>
              {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button type="submit" style={{ ...S.btn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
        <div style={S.footer}>
          Already have an account? <a href="/login" style={S.link}>Login</a>
        </div>
      </div>
    </div>
  );
};

const S = {
  page:     { minHeight: '100vh', background: '#f5f8f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'Open Sans', sans-serif" },
  card:     { background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px', width: '100%', maxWidth: '480px' },
  logo:     { height: '36px', marginBottom: '24px', display: 'block', margin: '0 auto 24px' },
  heading:  { fontFamily: "'Raleway', sans-serif", fontSize: '21px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 16px', textAlign: 'center' },
  badge:    { display: 'inline-flex', alignItems: 'center', background: '#eafaf1', color: '#27ae60', fontSize: '12px', fontWeight: '700', padding: '6px 14px', borderRadius: '20px', marginBottom: '20px', border: '1px solid #a9dfbf', width: 'fit-content' },
  referralBadge: { display: 'flex', alignItems: 'flex-start', background: '#eafaf1', color: '#1a6b5a', fontSize: '12px', fontWeight: '600', padding: '8px 14px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #a9dfbf', lineHeight: '1.6' },
  errorBox: { background: '#fdedec', color: '#e74c3c', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px', border: '1px solid #fadbd8' },
  row:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  field:    { marginBottom: '14px' },
  label:    { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', marginBottom: '5px', fontFamily: "'Raleway', sans-serif" },
  inputWrap:{ position: 'relative' },
  inputIcon:{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', display: 'flex', pointerEvents: 'none' },
  input:    { width: '100%', padding: '10px 12px 10px 34px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', outline: 'none' },
  btn:      { width: '100%', padding: '13px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: '8px', letterSpacing: '0.3px' },
  footer:      { textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#666' },
  link:        { color: '#4a8f82', fontWeight: '700', textDecoration: 'none' },
  appBanner:   { background: '#f0fff9', border: '1px solid #2ABFAB', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', textAlign: 'center' },
  appBannerText: { fontSize: '13px', color: '#2ABFAB', fontWeight: '600' },
  appBannerBtn:  { display: 'inline-block', background: '#2ABFAB', color: '#fff', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', fontFamily: "'Raleway', sans-serif" },
};

export default RegisterPatient;
